// components/invoice/InvoiceCard.tsx
import { useAppTheme } from "@/hooks/useAppTheme";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";

interface InvoiceCardProps {
  invoice: any;
  onRefresh?: () => void;
  onPrint?: (invoiceId: number) => void; // new
}

export default function InvoiceCard({
  invoice,
  onRefresh,
  onPrint,
}: InvoiceCardProps) {
  const { colors, spacing } = useAppTheme();

  const statusColor =
    invoice.status === "confirmed"
      ? "#22c55e"
      : invoice.status === "draft"
        ? "#f59e0b"
        : "#ef4444";

  const invoiceNumber = invoice.invoiceNo || invoice.invoiceNumber || "N/A";
  const invoiceDate = invoice.invoiceDate || invoice.date;
  const customerName =
    invoice.Customer?.fullName || invoice.customerName || "Unknown";
  let totalAmount =
    invoice.grandTotal ?? invoice.totalAmount ?? invoice.total ?? 0;
  if (typeof totalAmount !== "number")
    totalAmount = parseFloat(totalAmount) || 0;
  const status = invoice.status ?? "draft";

  const styles = StyleSheet.create({
    card: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    header: { flexDirection: "row", justifyContent: "space-between" },
    number: { fontSize: 16, fontWeight: "600", color: colors.text },
    date: { fontSize: 12, color: colors.textLight, marginTop: 2 },
    customer: { fontSize: 14, color: colors.text, marginTop: 4 },
    amount: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.primary,
      marginTop: 4,
    },
    statusBadge: { flexDirection: "row", alignItems: "center", marginTop: 8 },
    statusDot: { width: 8, height: 8, borderRadius: 4, marginRight: 6 },
    statusText: { fontSize: 12, fontWeight: "500", color: statusColor },
    footer: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: spacing.sm,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    printButton: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    printText: {
      fontSize: 12,
      color: colors.primary,
      fontWeight: "500",
    },
  });

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <View>
          <Text style={styles.number}>{invoiceNumber}</Text>
          <Text style={styles.date}>
            {invoiceDate
              ? new Date(invoiceDate).toLocaleDateString()
              : "No date"}
          </Text>
        </View>
        <TouchableOpacity
          onPress={() => router.push(`/(app)/invoices/${invoice.id}`)}
        >
          <Ionicons name="chevron-forward" size={20} color={colors.textLight} />
        </TouchableOpacity>
      </View>

      <Text style={styles.customer}>{customerName}</Text>
      <Text style={styles.amount}>₹{totalAmount.toFixed(2)}</Text>

      <View style={styles.statusBadge}>
        <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
        <Text style={styles.statusText}>{status.toUpperCase()}</Text>
      </View>

      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.printButton}
          onPress={() => onPrint?.(invoice.id)}
        >
          <Ionicons name="print-outline" size={18} color={colors.primary} />
          <Text style={styles.printText}>Print</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}
